import React, { useMemo, useRef } from 'react';
import {
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  Divider,
  IconButton,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import {
  Close,
  Search,
  Add,
  CalendarToday,
  Delete,
  Description,
  Download,
} from '@mui/icons-material';
import ComboBasic from '../../../../Components/MUIs/ComboBasic';
import 'ag-grid-enterprise';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { AgGridReact } from 'ag-grid-react';
import { getColumnDefs } from './REV_BASIC_ColumnDefs';
import Commons from '../../../../Services/Common/Common';

const tone = {
  bg: '#f7f9fb',
  surface: '#ffffff',
  surfaceLow: '#f0f4f7',
  border: 'rgba(172,179,183,0.25)',
  borderSoft: 'rgba(172,179,183,0.18)',
  primary: '#3856c4',
  primarySoft: 'rgba(56,86,196,0.10)',
  text: '#2c3437',
  muted: '#596064',
};

const priorityTone = (p) => {
  if (p === 'P1') return { bg: 'rgba(239,68,68,0.10)', fg: '#b91c1c' };
  if (p === 'P2') return { bg: 'rgba(59,130,246,0.10)', fg: '#1d4ed8' };
  return { bg: 'rgba(148,163,184,0.20)', fg: '#475569' };
};

const statusTone = (s) => {
  if (s === '시작전')
    return {
      bg: 'rgba(239,68,68,0.10)',
      fg: '#b91c1c',
      bd: 'rgba(239,68,68,0.25)',
    };
  if (s === '진행중')
    return {
      bg: 'rgba(168,85,247,0.10)',
      fg: '#6d28d9',
      bd: 'rgba(168,85,247,0.25)',
    };
  if (s === '완료')
    return {
      bg: 'rgba(148,163,184,0.20)',
      fg: '#475569',
      bd: 'rgba(148,163,184,0.35)',
    };
  if (s === '배포완료')
    return {
      bg: 'rgba(148,163,184,0.20)',
      fg: '#475569',
      bd: 'rgba(148,163,184,0.35)',
    };
  return {
    bg: 'rgba(148,163,184,0.20)',
    fg: '#475569',
    bd: 'rgba(148,163,184,0.35)',
  };
};

function REV_BASIC(props) {
  const {
    vendors,
    activeVendorId,
    onSelectVendor,
    vendorFilterText,
    onChangeVendorFilterText,
    tabs,
    activeTabId,
    onChangeTabId,
    gridQuickFilter,
    onChangeGridQuickFilter,
    statusFilter = 'all',
    onChangeStatusFilter,
    startCreateDate = '',
    endCreateDate = '',
    onChangeStartCreateDate,
    onChangeEndCreateDate,
    onDownloadExcel,
    rows,
    selectedRow,
    onSelectRow,
    selectedDetail,
    onCreateNew,
    onDelete,
    inlineDraft,
    onInlineChange,
    onInlineSave,
    inlineFileInputRef,
    onInlineAttachChange,
    inlinePendingFiles = [],
    onRemoveInlinePending,
    onDownloadServerFile,
    onDeleteServerFile,
    activityComment = '',
    onActivityCommentChange,
    onSubmitActivityComment,
    currentUserId = '',
    onDeleteActivityComment,
    assigneeComboItems = [],
    onOpenVersionLog,
  } = props;

  const REV_ATTACH_ACCEPT =
    '.pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.png,.jpg,.jpeg,.hwp';

  const priorityItems = useMemo(
    () => [
      ...['P1', 'P2', 'P3'].map((p) => {
        const t = priorityTone(p);
        return {
          value: p,
          name: (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  borderRadius: '9999px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 11,
                  fontWeight: 900,
                  bgcolor: t.bg,
                  color: t.fg,
                }}
              >
                {p}
              </Box>
            </Box>
          ),
        };
      }),
    ],
    []
  );

  const statusItems = useMemo(
    () => [
      ...['시작전', '진행중', '완료', '배포완료'].map((s) => {
        const t = statusTone(s);
        return {
          value: s,
          name: (
            <Chip
              label={s}
              size="small"
              sx={{
                height: 22,
                fontSize: 11,
                fontWeight: 800,
                bgcolor: t.bg,
                color: t.fg,
                border: `1px solid ${t.bd}`,
              }}
            />
          ),
        };
      }),
    ],
    []
  );

  const statusFilterItems = useMemo(
    () => [
      { name: '전체', value: 'all' },
      ...['시작전', '진행중', '완료', '배포완료'].map((s) => ({ name: s, value: s })),
    ],
    []
  );

  const categoryItems = useMemo(
    () => [
      { name: 'Web', value: 'Web' },
      { name: 'Mobile', value: 'Mobile' },
      { name: 'Common', value: 'Common' },
    ],
    []
  );

  const assigneeIdCurrent =
    inlineDraft?.assigneeId ?? selectedRow?.assigneeId ?? '';

  const assigneeAvatarLabel = useMemo(() => {
    const hit = (assigneeComboItems ?? []).find(
      (it) => String(it.value ?? '') === String(assigneeIdCurrent ?? '')
    );
    const nm = hit?.name;
    if (typeof nm === 'string' && nm.trim()) return nm.trim();
    if (!assigneeIdCurrent) return '';
    return String(assigneeIdCurrent);
  }, [assigneeComboItems, assigneeIdCurrent]);

  const assigneeAvatarText = useMemo(() => {
    const s = String(assigneeAvatarLabel ?? '').trim();
    if (!s) return '?';
    return s.slice(0, 2).toUpperCase();
  }, [assigneeAvatarLabel]);

  const gridRef = useRef(null);

  const columnDefs = useMemo(() => getColumnDefs(), []);

  const overdueBaseStyle = useMemo(
    () => ({ backgroundColor: 'rgba(239,68,68,0.06)' }),
    []
  );

  const filteredVendors = useMemo(() => {
    const q = String(vendorFilterText ?? '')
      .trim()
      .toLowerCase();
    if (!q) return vendors;
    return (vendors ?? []).filter((v) =>
      String(v?.name ?? '')
        .toLowerCase()
        .includes(q)
    );
  }, [vendors, vendorFilterText]);

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        width: '100%',
        bgcolor: tone.bg,
        display: 'flex',
        overflow: 'hidden',
      }}
    >
      {/* Left: Vendor Filter */}
      <Box
        sx={{
          width: 288,
          bgcolor: tone.surfaceLow,
          borderRight: `1px solid ${tone.borderSoft}`,
          display: 'flex',
          flexDirection: 'column',
          flexShrink: 0,
          height: '100%',
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        <Box sx={{ p: 2.5, borderBottom: `1px solid ${tone.borderSoft}` }}>
          <Typography
            sx={{ fontSize: 18, fontWeight: 800, color: tone.text, mb: 1.5 }}
          >
            회사 정보
          </Typography>
          <TextField
            size="small"
            fullWidth
            placeholder="Filter vendors..."
            value={vendorFilterText}
            onChange={(e) => onChangeVendorFilterText?.(e.target.value)}
            InputProps={{
              startAdornment: (
                <Search sx={{ fontSize: 18, color: tone.muted, mr: 1 }} />
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: '#dce4e8',
                borderRadius: 1.5,
                '& fieldset': { borderColor: 'transparent' },
                '&:hover fieldset': { borderColor: 'transparent' },
                '&.Mui-focused fieldset': { borderColor: 'transparent' },
              },
              '& input': { fontSize: 12 },
            }}
          />
        </Box>

        <Box sx={{ p: 1.5, overflowY: 'auto' }}>
          {(filteredVendors ?? []).map((v) => {
            const active = v.id === activeVendorId;
            return (
              <Button
                key={v.id}
                fullWidth
                onClick={() => onSelectVendor?.(v.id)}
                sx={{
                  justifyContent: 'space-between',
                  textTransform: 'none',
                  p: 1.5,
                  mb: 0.5,
                  borderRadius: 2,
                  bgcolor: active ? '#dce4e8' : 'transparent',
                  color: active ? tone.text : tone.muted,
                  '&:hover': {
                    bgcolor: active ? '#dce4e8' : 'rgba(220,228,232,0.6)',
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '9999px',
                      bgcolor: active
                        ? 'rgba(109,137,250,0.18)'
                        : 'rgba(224,224,249,0.65)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: tone.primary,
                      fontSize: 12,
                      fontWeight: 900,
                    }}
                  >
                    {v.initials}
                  </Box>
                  <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
                    {v.name}
                  </Typography>
                </Box>
                <Chip
                  label={String(v.badge ?? 0)}
                  size="small"
                  sx={{
                    height: 18,
                    fontSize: 10,
                    fontWeight: 900,
                    bgcolor:
                      Number(v.badge ?? 0) > 0
                        ? 'rgba(249,115,134,0.18)'
                        : '#dce4e8',
                    color: Number(v.badge ?? 0) > 0 ? '#a8364b' : tone.muted,
                  }}
                />
              </Button>
            );
          })}
        </Box>
      </Box>

      {/* Center: Board */}
      <Box
        sx={{
          flex: 1,
          minWidth: 520,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ p: 3, borderBottom: `1px solid ${tone.borderSoft}` }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: 2,
              mb: 2,
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontSize: 24,
                  fontWeight: 900,
                  color: tone.text,
                  mb: 0.5,
                }}
              >
                요구사항
              </Typography>
              <Typography sx={{ fontSize: 13, color: tone.muted }}>
                공급업체 결과를 관리 및 추적
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button
                variant="outlined"
                onClick={() => onOpenVersionLog?.()}
                sx={{ fontWeight: 900, borderRadius: 2 }}
              >
                버전 로그
              </Button>
              <Button
                variant="contained"
                startIcon={<Add sx={{ fontSize: 18 }} />}
                onClick={() => onCreateNew?.()}
                sx={{
                  bgcolor: tone.primary,
                  fontWeight: 800,
                  borderRadius: 2,
                  px: 2,
                  '&:hover': { bgcolor: '#2949b7' },
                }}
              >
                새로운 요구사항
              </Button>
            </Box>
          </Box>

          <Tabs
            value={activeTabId}
            onChange={(_, v) => onChangeTabId?.(v)}
            variant="scrollable"
            scrollButtons={false}
            sx={{
              minHeight: 36,
              '& .MuiTab-root': {
                minHeight: 36,
                textTransform: 'none',
                fontSize: 13,
                fontWeight: 800,
                color: tone.muted,
                px: 1.5,
              },
              '& .Mui-selected': { color: tone.primary },
              '& .MuiTabs-indicator': {
                backgroundColor: tone.primary,
                height: 2,
              },
            }}
          >
            {(tabs ?? []).map((t) => (
              <Tab key={t.id} value={t.id} label={t.label} />
            ))}
          </Tabs>
        </Box>

        <Box
          sx={{
            p: 3,
            flex: 1,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Card
            sx={{
              mb: 1.5,
              p: 1.5,
              borderRadius: 2.5,
              border: `1px solid ${tone.borderSoft}`,
              boxShadow: '0 2px 12px rgba(44,52,55,0.04)',
            }}
          >
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                size="small"
                fullWidth
                placeholder="Search..."
                value={gridQuickFilter}
                onChange={(e) => {
                  const v = e.target.value;
                  onChangeGridQuickFilter?.(v);
                  if (gridRef.current?.api) gridRef.current.api.setQuickFilter(v);
                }}
                InputProps={{
                  startAdornment: (
                    <Search sx={{ fontSize: 18, color: tone.muted, mr: 1 }} />
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: '#f0f4f7',
                    borderRadius: 2,
                    '& fieldset': { borderColor: 'transparent' },
                    '&:hover fieldset': { borderColor: 'transparent' },
                    '&.Mui-focused fieldset': { borderColor: 'transparent' },
                  },
                  '& input': { fontSize: 13 },
                }}
              />

              <ComboBasic
                size="small"
                fullWidth={false}
                name="상태"
                value={statusFilter}
                items={statusFilterItems}
                onChange={(v) => onChangeStatusFilter?.(v)}
                wrapSx={{
                  minWidth: 140,
                  '& .MuiOutlinedInput-root': {
                    bgcolor: '#f0f4f7',
                    borderRadius: 2,
                    '& fieldset': { borderColor: 'transparent' },
                    '&:hover fieldset': { borderColor: 'transparent' },
                    '&.Mui-focused fieldset': { borderColor: 'transparent' },
                  },
                  '& .MuiSelect-select': { fontSize: 13 },
                }}
              />

              <Button
                variant="outlined"
                onClick={() => onDownloadExcel?.()}
                sx={{ fontWeight: 900, borderRadius: 2, whiteSpace: 'nowrap' }}
              >
                엑셀
              </Button>
            </Box>

            <Box
              sx={{
                display: 'flex',
                gap: 1,
                mt: 1,
                alignItems: 'center',
                flexWrap: 'wrap',
              }}
            >
              <TextField
                size="small"
                type="date"
                label="등록 시작"
                value={startCreateDate}
                onChange={(e) => onChangeStartCreateDate?.(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{
                  minWidth: 160,
                  '& .MuiOutlinedInput-root': {
                    bgcolor: '#f0f4f7',
                    borderRadius: 2,
                    '& fieldset': { borderColor: 'transparent' },
                    '&:hover fieldset': { borderColor: 'transparent' },
                    '&.Mui-focused fieldset': { borderColor: 'transparent' },
                  },
                }}
              />
              <TextField
                size="small"
                type="date"
                label="등록 종료"
                value={endCreateDate}
                onChange={(e) => onChangeEndCreateDate?.(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{
                  minWidth: 160,
                  '& .MuiOutlinedInput-root': {
                    bgcolor: '#f0f4f7',
                    borderRadius: 2,
                    '& fieldset': { borderColor: 'transparent' },
                    '&:hover fieldset': { borderColor: 'transparent' },
                    '&.Mui-focused fieldset': { borderColor: 'transparent' },
                  },
                }}
              />

            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <Box
                sx={{
                  width: 14,
                  height: 14,
                  borderRadius: 0.75,
                  bgcolor: 'rgba(239,68,68,0.10)',
                  border: '1px solid rgba(239,68,68,0.18)',
                }}
              />
              <Typography sx={{ fontSize: 12, color: tone.muted, fontWeight: 700 }}>
                마감일이 오늘 이전인 항목은 배경색으로 표시됩니다.
              </Typography>
            </Box>
          </Card>

          <Card
            sx={{
              flex: 1,
              borderRadius: 2.5,
              border: `1px solid ${tone.borderSoft}`,
              boxShadow: '0 2px 12px rgba(44,52,55,0.04)',
              overflow: 'hidden',
            }}
          >
            <Box sx={{ height: '100%' }}>
              <div
                className="ag-theme-alpine"
                style={{ height: '100%', width: '100%' }}
              >
                <AgGridReact
                  ref={gridRef}
                  rowData={rows}
                  columnDefs={columnDefs}
                  getRowId={(p) =>
                    String(p.data?._agGridRowId ?? p.data?.reqSeq ?? '')
                  }
                  defaultColDef={{
                    resizable: true,
                    sortable: true,
                    filter: true,
                  }}
                  headerHeight={42}
                  rowHeight={52}
                  rowSelection="single"
                  suppressRowClickSelection={true}
                  onRowClicked={(p) => {
                    if (!p?.data) return;
                    onSelectRow?.(p.data);
                  }}
                  getRowStyle={(p) => {
                    if (!p?.data) return null;
                    if (
                      selectedRow?.reqSeq &&
                      Number(p.data.reqSeq) === Number(selectedRow.reqSeq)
                    ) {
                      return { backgroundColor: 'rgba(240,244,247,1)' };
                    }

                    const due = String(p.data?.dueDate ?? '').substring(0, 10);
                    if (due) {
                      const dueDt = new Date(`${due}T00:00:00`);
                      const now = new Date();
                      const today0 = new Date(
                        now.getFullYear(),
                        now.getMonth(),
                        now.getDate()
                      );
                      if (!Number.isNaN(dueDt.getTime()) && dueDt < today0) {
                        return overdueBaseStyle;
                      }
                    }
                    return null;
                  }}
                />
              </div>
            </Box>
          </Card>
        </Box>
      </Box>

      {/* Right: Detail Panel */}
      <Box
        sx={{
          width: 384,
          bgcolor: '#ffffff',
          borderLeft: `1px solid ${tone.borderSoft}`,
          display: 'flex',
          flexDirection: 'column',
          flexShrink: 0,
          height: '100%',
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            p: 2.5,
            borderBottom: `1px solid ${tone.borderSoft}`,
            display: 'flex',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Chip
                label={inlineDraft?.status ?? selectedRow?.status ?? '—'}
                size="small"
                sx={{
                  height: 20,
                  fontSize: 10,
                  fontWeight: 900,
                  ...((inlineDraft?.status ?? selectedRow?.status)
                    ? (() => {
                        const t = statusTone(
                          inlineDraft?.status ?? selectedRow?.status
                        );
                        return { bgcolor: t.bg, color: t.fg };
                      })()
                    : { bgcolor: 'rgba(220,228,232,0.9)', color: tone.muted }),
                }}
              />
              <Typography sx={{ fontSize: 12, color: tone.muted }}>
                {selectedRow?.reqSeq ? `ID-${selectedRow.reqSeq}` : 'ID-—'}
              </Typography>
            </Box>
            <Typography
              sx={{
                fontSize: 18,
                fontWeight: 900,
                color: tone.text,
                lineHeight: 1.25,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
              title={selectedRow?.title ?? ''}
            >
              {inlineDraft?.title ??
                selectedRow?.title ??
                '선택된 항목이 없습니다'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              size="small"
              variant="contained"
              onClick={() => onInlineSave?.()}
              disabled={!inlineDraft?.reqSeq}
              sx={{
                bgcolor: tone.primary,
                fontWeight: 900,
                borderRadius: 2,
                '&:hover': { bgcolor: '#2949b7' },
              }}
            >
              저장
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="error"
              onClick={() => onDelete?.()}
              disabled={!selectedRow?.reqSeq}
              sx={{ fontWeight: 800 }}
            >
              삭제
            </Button>
          </Box>
        </Box>

        <Box sx={{ p: 2.5, overflowY: 'auto', flex: 1 }}>
          <Card
            sx={{
              p: 2,
              borderRadius: 2.5,
              border: `1px solid ${tone.borderSoft}`,
              boxShadow: 'none',
              mb: 3,
            }}
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '96px 1fr',
                rowGap: 1.5,
                columnGap: 1.5,
                alignItems: 'center',
              }}
            >
              <Typography
                sx={{ fontSize: 12, fontWeight: 700, color: tone.muted }}
              >
                담당자
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Avatar
                  sx={{
                    width: 24,
                    height: 24,
                    fontSize: 11,
                    bgcolor: tone.primarySoft,
                    color: tone.primary,
                  }}
                >
                  {assigneeAvatarText}
                </Avatar>
                <ComboBasic
                  size="small"
                  fullWidth
                  name=""
                  value={String(inlineDraft?.assigneeId ?? '')}
                  items={assigneeComboItems}
                  onChange={(v) =>
                    onInlineChange?.({ assigneeId: String(v ?? '') })
                  }
                  wrapSx={{
                    flex: 1,
                    '& .MuiInputLabel-root': { display: 'none' },
                    '& .MuiOutlinedInput-root': {
                      bgcolor: '#f7f9fb',
                      '& fieldset': { borderColor: 'rgba(172,179,183,0.25)' },
                    },
                    '& .MuiSelect-select': {
                      fontSize: 13,
                      fontWeight: 700,
                      py: 1,
                    },
                  }}
                  sx={{}}
                />
              </Box>

              <Typography
                sx={{ fontSize: 12, fontWeight: 700, color: tone.muted }}
              >
                마감일
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarToday sx={{ fontSize: 16, color: '#a8364b' }} />
                <TextField
                  size="small"
                  type="date"
                  value={inlineDraft?.dueDate ?? ''}
                  onChange={(e) =>
                    onInlineChange?.({ dueDate: e.target.value })
                  }
                  sx={{
                    flex: 1,
                    '& .MuiOutlinedInput-root': {
                      bgcolor: '#f7f9fb',
                      '& fieldset': { borderColor: 'rgba(172,179,183,0.25)' },
                    },
                    '& input': {
                      fontSize: 13,
                      fontWeight: 800,
                      color: '#a8364b',
                    },
                  }}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>

              <Typography
                sx={{ fontSize: 12, fontWeight: 700, color: tone.muted }}
              >
                우선순위
              </Typography>
              <ComboBasic
                size="small"
                fullWidth
                name=""
                value={inlineDraft?.priority ?? 'P2'}
                items={priorityItems}
                onChange={(v) => onInlineChange?.({ priority: v })}
                wrapSx={{
                  '& .MuiInputLabel-root': { display: 'none' },
                  '& .MuiOutlinedInput-root': {
                    bgcolor: '#f7f9fb',
                    '& fieldset': { borderColor: 'rgba(172,179,183,0.25)' },
                  },
                  '& .MuiSelect-select': {
                    fontSize: 13,
                    fontWeight: 800,
                    py: 1,
                  },
                }}
                sx={{}}
              />

              <Typography
                sx={{ fontSize: 12, fontWeight: 700, color: tone.muted }}
              >
                상태
              </Typography>
              <ComboBasic
                size="small"
                fullWidth
                name=""
                value={inlineDraft?.status ?? '시작전'}
                items={statusItems}
                onChange={(v) => onInlineChange?.({ status: v })}
                wrapSx={{
                  '& .MuiInputLabel-root': { display: 'none' },
                  '& .MuiOutlinedInput-root': {
                    bgcolor: '#f7f9fb',
                    '& fieldset': { borderColor: 'rgba(172,179,183,0.25)' },
                  },
                  '& .MuiSelect-select': {
                    fontSize: 13,
                    fontWeight: 800,
                    py: 1,
                  },
                }}
                sx={{}}
              />

              <Typography
                sx={{ fontSize: 12, fontWeight: 700, color: tone.muted }}
              >
                카테고리
              </Typography>
              <ComboBasic
                size="small"
                fullWidth
                name=""
                value={inlineDraft?.category ?? ''}
                items={categoryItems}
                onChange={(v) => onInlineChange?.({ category: v })}
                wrapSx={{
                  '& .MuiInputLabel-root': { display: 'none' },
                  '& .MuiOutlinedInput-root': {
                    bgcolor: '#f7f9fb',
                    '& fieldset': { borderColor: 'rgba(172,179,183,0.25)' },
                  },
                  '& .MuiSelect-select': {
                    fontSize: 13,
                    fontWeight: 800,
                    py: 1,
                  },
                }}
                sx={{}}
              />
            </Box>
          </Card>

          <Box sx={{ mb: 3 }}>
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 900,
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                color: tone.muted,
                mb: 1.25,
              }}
            >
              설명
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={4}
              value={inlineDraft?.content ?? ''}
              onChange={(e) => onInlineChange?.({ content: e.target.value })}
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: '#f7f9fb',
                  '& fieldset': { borderColor: 'rgba(172,179,183,0.25)' },
                },
                '& textarea': { fontSize: 13, lineHeight: 1.65 },
              }}
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 900,
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                color: tone.muted,
                mb: 1.25,
              }}
            >
              첨부 파일
            </Typography>
            <Paper
              elevation={1}
              sx={{
                p: 1.5,
                mb: 1.5,
                border: `1px solid ${tone.borderSoft}`,
                bgcolor: tone.surface,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <TextField
                  placeholder="파일 선택"
                  variant="outlined"
                  size="small"
                  fullWidth
                  value="파일 선택"
                  onClick={() => inlineFileInputRef?.current?.click()}
                  inputProps={{
                    readOnly: true,
                    tabIndex: -1,
                    style: { caretColor: 'transparent' },
                  }}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: '#f7f9fb',
                      '& fieldset': { borderColor: 'rgba(172,179,183,0.25)' },
                    },
                  }}
                />
                <input
                  type="file"
                  ref={inlineFileInputRef}
                  onChange={onInlineAttachChange}
                  style={{ display: 'none' }}
                  accept={REV_ATTACH_ACCEPT}
                />
              </Stack>
            </Paper>

            {inlinePendingFiles.length > 0 && (
              <Stack spacing={1} sx={{ mb: 1.5 }}>
                {inlinePendingFiles.map((p) => (
                  <Stack
                    key={p.clientId}
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{
                      p: 1,
                      borderRadius: 2,
                      border: `1px solid ${tone.borderSoft}`,
                      bgcolor: tone.surfaceLow,
                    }}
                  >
                    <Description sx={{ fontSize: 18, color: tone.muted }} />
                    <Typography
                      sx={{
                        flex: 1,
                        fontSize: 12,
                        fontWeight: 700,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={p.fileName}
                    >
                      {p.fileName}
                    </Typography>
                    <Typography
                      sx={{ fontSize: 10, color: tone.muted, flexShrink: 0 }}
                    >
                      업로드 대기
                    </Typography>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => onRemoveInlinePending?.(p.clientId)}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Stack>
                ))}
              </Stack>
            )}

            <Stack spacing={1}>
              {(selectedDetail?.files ?? []).map((f) => {
                const fileName = String(f?.fileName ?? '');
                return (
                  <Stack
                    key={String(f.fileSeq ?? fileName)}
                    direction="row"
                    alignItems="center"
                    spacing={0.5}
                    sx={{
                      p: 1,
                      borderRadius: 2,
                      border: `1px solid ${tone.borderSoft}`,
                      bgcolor: tone.surface,
                    }}
                  >
                    <Button
                      size="small"
                      variant="text"
                      onClick={() =>
                        onDownloadServerFile?.({
                          fileName: f.fileName,
                          filePath: f.filePath,
                        })
                      }
                      sx={{
                        flex: 1,
                        justifyContent: 'flex-start',
                        textTransform: 'none',
                        fontWeight: 700,
                        fontSize: 12,
                        color: tone.primary,
                        minWidth: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={fileName}
                    >
                      {fileName || '(이름 없음)'}
                    </Button>
                    <IconButton
                      size="small"
                      sx={{ color: tone.primary }}
                      onClick={() =>
                        onDownloadServerFile?.({
                          fileName: f.fileName,
                          filePath: f.filePath,
                        })
                      }
                      aria-label="다운로드"
                    >
                      <Download fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => onDeleteServerFile?.(f)}
                      aria-label="삭제"
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Stack>
                );
              })}
            </Stack>
            {(selectedDetail?.files ?? []).length === 0 &&
              inlinePendingFiles.length === 0 && (
                <Typography sx={{ fontSize: 13, color: tone.muted, mt: 1 }}>
                  첨부파일이 없습니다.
                </Typography>
              )}
          </Box>

          <Divider sx={{ borderColor: tone.borderSoft, mb: 2.5 }} />

          <Box sx={{ mb: 2 }}>
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 900,
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                color: tone.muted,
                mb: 0.5,
              }}
            >
              활동 (댓글)
            </Typography>
            <Typography sx={{ fontSize: 12, color: tone.muted, mb: 1.25 }}>
              로그인한 사용자 누구나 이 요구사항에 댓글을 남길 수 있습니다.
            </Typography>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              alignItems={{ xs: 'stretch', sm: 'flex-start' }}
              sx={{ mb: 2 }}
            >
              <TextField
                fullWidth
                multiline
                minRows={2}
                maxRows={8}
                size="small"
                placeholder="댓글을 입력하세요..."
                value={activityComment}
                onChange={(e) => onActivityCommentChange?.(e.target.value)}
                disabled={!selectedRow?.reqSeq}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                    e.preventDefault();
                    onSubmitActivityComment?.();
                  }
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: '#f7f9fb',
                    '& fieldset': { borderColor: 'rgba(172,179,183,0.25)' },
                  },
                }}
              />
              <Button
                variant="contained"
                onClick={() => onSubmitActivityComment?.()}
                disabled={!selectedRow?.reqSeq}
                sx={{
                  bgcolor: tone.primary,
                  fontWeight: 800,
                  px: 2,
                  flexShrink: 0,
                  alignSelf: { xs: 'stretch', sm: 'flex-start' },
                  '&:hover': { bgcolor: '#2949b7' },
                }}
              >
                등록
              </Button>
            </Stack>
            <Typography sx={{ fontSize: 10, color: tone.muted, mb: 1 }}>
              Ctrl+Enter로 등록할 수 있습니다.
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {(selectedDetail?.activities ?? []).map((a, idx) => {
                const isMine =
                  String(a?.userId ?? '')
                    .trim()
                    .toLowerCase() ===
                  String(Commons.DecryptoAES(currentUserId) ?? '')
                    .trim()
                    .toLowerCase();
                return (
                  <Box
                    key={`${idx}-${a?.actSeq ?? ''}`}
                    sx={{
                      display: 'flex',
                      gap: 1.25,
                      alignItems: 'flex-start',
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 24,
                        height: 24,
                        fontSize: 11,
                        bgcolor: 'rgba(220,228,232,0.9)',
                        color: tone.muted,
                        mt: 0.5,
                      }}
                    >
                      {String(a?.userId ?? '?')
                        .substring(0, 2)
                        .toUpperCase()}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 1,
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'baseline',
                            gap: 1,
                            flexWrap: 'wrap',
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: 13,
                              fontWeight: 800,
                              color: tone.text,
                            }}
                          >
                            {a?.userId ?? '—'}
                          </Typography>
                          <Typography sx={{ fontSize: 10, color: tone.muted }}>
                            {a?.createDate
                              ? String(a.createDate)
                                  .replace('T', ' ')
                                  .substring(0, 16)
                              : ''}
                          </Typography>
                        </Box>
                        {isMine && (
                          <IconButton
                            size="small"
                            color="error"
                            aria-label="댓글 삭제"
                            onClick={() => onDeleteActivityComment?.(a)}
                            sx={{ mt: -0.5 }}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                      <Box
                        sx={{
                          mt: 0.75,
                          p: 1.25,
                          borderRadius: 2,
                          border: `1px solid ${tone.borderSoft}`,
                          bgcolor: tone.surface,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 12,
                            color: tone.muted,
                            lineHeight: 1.6,
                          }}
                        >
                          {a?.comment ?? ''}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                );
              })}
              {(selectedDetail?.activities ?? []).length === 0 && (
                <Typography sx={{ fontSize: 13, color: tone.muted }}>
                  활동 내역이 없습니다.
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default REV_BASIC;
